import CustomCard from '@/components/customCard';
import { getArrayDateFromStartDateToEndDate } from '@/helpers/time';
import useUserPlan from '@/hooks/useUserPlan';
import { LineChart, PolarisVizProvider } from '@shopify/polaris-viz';
import { useMemo } from 'react';

export interface IDataAnalytic {
  ruleId?: number;
  createdAt?: string;
  totalAddToCart: number;
  totalBannerClicks: number;
  totalBannerImpressions: number;
  totalHoverBannerETA: number;
  totalVisitorsProductPage: number;
}

interface IDataFormatted {
  [key: string]: IDataAnalytic;
}

interface IProps {
  data: IDataAnalytic[];
  startDate: Date;
  endDate: Date;
}

const OverTime = ({ data, startDate, endDate }: IProps) => {
  const { isFreePlan } = useUserPlan();

  const dataFormatted: IDataFormatted = useMemo(() => {
    const arrayDateFromStartDateToEndDate = getArrayDateFromStartDateToEndDate(startDate, endDate);
    const init: IDataFormatted = {};
    arrayDateFromStartDateToEndDate.forEach((item) => {
      init[item] = {
        totalAddToCart: 0,
        totalBannerClicks: 0,
        totalBannerImpressions: 0,
        totalHoverBannerETA: 0,
        totalVisitorsProductPage: 0,
      };
    });

    data?.forEach((item) => {
      if (item.createdAt) {
        const { totalAddToCart, totalBannerClicks, totalBannerImpressions, totalHoverBannerETA, totalVisitorsProductPage } = item;
        const currentDate = new Date(item.createdAt).toLocaleDateString('en-US');
        const currentData: IDataAnalytic = {
          totalAddToCart: totalAddToCart + (init[currentDate]?.totalAddToCart || 0),
          totalBannerClicks: totalBannerClicks + (init[currentDate]?.totalBannerClicks || 0),
          totalBannerImpressions: totalBannerImpressions + (init[currentDate]?.totalBannerImpressions || 0),
          totalHoverBannerETA: totalHoverBannerETA + (init[currentDate]?.totalHoverBannerETA || 0),
          totalVisitorsProductPage: totalVisitorsProductPage + (init[currentDate]?.totalVisitorsProductPage || 0),
        };
        init[currentDate] = currentData;
      }
    });
    return init;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data), startDate, endDate]);

  const dataChart = useMemo(() => {
    if (data) {
      const listKey = ['PDP visits', 'Impression', 'Hover', 'Click', 'Add to Cart'];
      const dataAnalytics = Object.entries(dataFormatted).map(([date, value]) => [
        value?.totalVisitorsProductPage || 0,
        value?.totalBannerImpressions || 0,
        value?.totalHoverBannerETA || 0,
        value?.totalBannerClicks || 0,
        value?.totalAddToCart || 0,
      ]);
      const categories = Object.keys(dataFormatted);
      const colors = ['#86A4FA', '#14B8FA', '#00BFB3', '#EB9B29', '#FF757D'];
      const result = listKey.map((key, index) => {
        return {
          name: key,
          color: colors[index],
          data: dataAnalytics.map((value, i) => {
            const currentCategory = categories[i] ? new Date(categories[i]) : '';
            return {
              value: value[index],
              key: currentCategory ? `${currentCategory.getDate()}/${currentCategory.getMonth() + 1}` : '',
            };
          }),
        };
      });
      return result;
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFormatted]);

  return (
    <CustomCard title={<b style={{ fontSize: '16px' }}>Performance over time</b>} cardClassName={isFreePlan ? 'disabled' : ''}>
      <PolarisVizProvider>
        <div style={{ marginTop: '1rem' }} className="wrap-chart">
          <LineChart theme="Light" data={dataChart} />
        </div>
      </PolarisVizProvider>
    </CustomCard>
  );
};

export default OverTime;
